/**
 * Iter 68 — Vendor No-Show panel.
 *
 * Two cards in one mount:
 *   1. Policy editor — grace period (30 / 60 / 120 / custom minutes),
 *      default no-show fee in EUR, refund policy (none / partial / full).
 *      All saved to /api/vendor/no-show-policy.
 *   2. Stats tile — last-365-days count, lost revenue, fee recovered,
 *      and the vendor's top-3 frequent no-show customers (so they can
 *      decide to require prepayment from those repeaters going forward).
 *
 * Used inside the vendor dashboard's existing Settings / Policies area.
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { UserX, Save, Loader2, TrendingDown, Users } from "lucide-react";
import { toast } from "sonner";

const GRACE_PRESETS = [
  { value: "30", label: "30 minutes" },
  { value: "60", label: "1 hour" },
  { value: "120", label: "2 hours" },
];

export default function VendorNoShowPanel() {
  const [policy, setPolicy] = useState(null);
  const [stats, setStats] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [p, s] = await Promise.all([
        api.get("/vendor/no-show-policy").then((r) => r.data),
        api.get("/vendor/no-show-stats?days=365").then((r) => r.data),
      ]);
      setPolicy(p);
      setStats(s);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const fresh = await api.put("/vendor/no-show-policy", {
        no_show_grace_minutes: Number(policy.no_show_grace_minutes),
        no_show_fee_eur: Number(policy.no_show_fee_eur),
        no_show_refund_policy: policy.no_show_refund_policy,
      }).then((r) => r.data);
      setPolicy(fresh);
      toast.success("No-show policy saved.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!policy) return null;

  // Translate the saved minutes into the preset value (so the Select
  // pre-selects the matching radio); anything not in the presets falls
  // through to the custom-minute input below.
  const presetValue = GRACE_PRESETS.find((p) => Number(p.value) === Number(policy.no_show_grace_minutes))?.value || "custom";

  return (
    <div className="space-y-6" data-testid="vendor-no-show-panel">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserX className="h-5 w-5 text-violet-600" /> No-show policy
          </CardTitle>
          <CardDescription>
            Configure how soon after pickup time you can mark a renter as a No Show,
            the fee that gets appended to their invoice, and the refund treatment.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label htmlFor="no-show-grace">Grace period after pickup time</Label>
              <Select
                value={presetValue}
                onValueChange={(v) => {
                  if (v === "custom") return;
                  setPolicy({ ...policy, no_show_grace_minutes: Number(v) });
                }}
              >
                <SelectTrigger data-testid="no-show-grace-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {GRACE_PRESETS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                  <SelectItem value="custom">Custom (minutes)</SelectItem>
                </SelectContent>
              </Select>
              {presetValue === "custom" && (
                <Input
                  id="no-show-grace"
                  type="number"
                  min={0}
                  max={1440}
                  value={policy.no_show_grace_minutes}
                  onChange={(e) => setPolicy({ ...policy, no_show_grace_minutes: Number(e.target.value) })}
                  data-testid="no-show-grace-custom"
                />
              )}
              <p className="text-xs text-neutral-500">
                We block the "Mark No Show" action until this many minutes
                have elapsed past the scheduled pickup time.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="no-show-fee">Default no-show fee (EUR)</Label>
              <Input
                id="no-show-fee"
                type="number"
                min={0}
                step="0.01"
                value={policy.no_show_fee_eur}
                onChange={(e) => setPolicy({ ...policy, no_show_fee_eur: Number(e.target.value) })}
                data-testid="no-show-fee-input"
              />
              <p className="text-xs text-neutral-500">
                Appended as a line item on the rental invoice when you mark a
                no-show. <strong>If a Stripe security deposit is still on
                hold, this amount is auto-captured from it</strong> — the
                rest of the deposit is refunded to the customer. When no
                deposit is on file the invoice line still serves as a
                billable record you can collect offline.
              </p>
            </div>
          </div>

          <div className="space-y-2 max-w-md">
            <Label htmlFor="no-show-refund">Refund policy on No Show</Label>
            <Select
              value={policy.no_show_refund_policy}
              onValueChange={(v) => setPolicy({ ...policy, no_show_refund_policy: v })}
            >
              <SelectTrigger id="no-show-refund" data-testid="no-show-refund-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No refund (keep full payment)</SelectItem>
                <SelectItem value="partial">Partial refund (vendor decides)</SelectItem>
                <SelectItem value="full">Full refund</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button onClick={save} disabled={saving} data-testid="no-show-policy-save">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save policy
          </Button>
        </CardContent>
      </Card>

      {stats && (
        <Card data-testid="vendor-no-show-stats">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <TrendingDown className="h-4 w-4 text-rose-600" /> No-show stats · last {stats.window_days} days
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <div className="text-xs uppercase tracking-wider text-neutral-500">No-shows</div>
                <div className="text-2xl font-semibold mt-1" data-testid="stat-no-show-count">{stats.count}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-neutral-500">Lost revenue</div>
                <div className="text-2xl font-semibold mt-1 text-rose-700">€{stats.lost_revenue_eur.toFixed(2)}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-neutral-500">Fee recovered</div>
                <div className="text-2xl font-semibold mt-1 text-emerald-700">€{stats.fee_recovered_eur.toFixed(2)}</div>
              </div>
            </div>

            {stats.top_repeat?.length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-wider text-neutral-500 flex items-center gap-1.5 mb-2">
                  <Users className="h-3 w-3" /> Frequent no-shows
                </div>
                <ul className="space-y-1.5">
                  {stats.top_repeat.map((r) => (
                    <li key={r.customer_id} className="flex items-center justify-between text-sm">
                      <span className="text-neutral-700">{r.name}{r.email ? ` · ${r.email}` : ""}</span>
                      <Badge variant="secondary" className="bg-violet-50 text-violet-800 border-violet-200">
                        {r.count}× no-show
                      </Badge>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
